import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, inject, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { scaleThreshold } from 'd3-scale';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { map, startWith, take } from 'rxjs';
import { AppService } from '../../../../services/app.service';
import { MapService } from '../../../../services/map.service';
import { MetricLayerFeature, RestorationService } from '../../../../services/restoration.service';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import { MetricDetails } from '../../../classes/metric-details';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';
import { PopoverData } from '../../../classes/popover-data';
import { IceLayerFeature } from '../../../../services/ice.service';

interface Metric {
  displayName: string;
  value: string;
  measurement: string;
}

@Component({
  selector: 'tgrid-restoration-metric-layer',
  imports: [
    MatExpansionModule,
    MatSlideToggleModule,
    MatSliderModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatFormFieldModule,
    DecimalPipe,
    TitleCasePipe,
    LayerHeaderComponent,
  ],
  templateUrl: './restoration-metric-layer.component.html',
  standalone: true,
  styleUrl: './restoration-metric-layer.component.scss',
})
export class RestorationMetricLayerComponent extends BaseUILayer implements AfterViewInit {

  private restorationService = inject(RestorationService);
  private appService = inject(AppService);
  private numberPipe = new DecimalPipe('en-US');
  //to change the legend, just change this list to what colors you want. Domain is automatically bucketed to colors.
  private colors = ['#e8cc65', '#D48B43', '#c14a22'];

  public legendMin: number;
  public legendMax: number;

  protected metrics: Metric[] = [
    { value: 'impact_level', displayName: 'Customers Impacted', measurement: 'customers' },
    { value: 'recovery_duration', displayName: 'Recovery Duration', measurement: 'minutes' },
    { value: 'auc', displayName: 'Area Under the Curve', measurement: 'customer-minutes' },
  ];

  public filterGroup = new FormGroup({
    metric: new FormControl<Metric>(this.metrics[0], { nonNullable: true }),
    aggregation: new FormControl<'min' | 'max' | 'avg' | 'sum'>('avg', { nonNullable: true }),
    measurement: new FormControl(''),
    includeNtlData: new FormControl(true, { nonNullable: true }),
  });

  constructor() {
    super();
    const filterSignal = toSignal(this.filterGroup.valueChanges.pipe(startWith(this.filterGroup.value)));
    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      filterSignal();
      if (this.checked) {
        MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
        this.getMetricData();
      }
    });
  }

  @ViewChild('legendBar') legendBarElement!: ElementRef<HTMLDivElement>;
  ngAfterViewInit(): void {
    this.legendBarElement.nativeElement.style.background = `linear-gradient(to right, ${this.colors.join(',')})`;
  }
  override toggleLayer(checked: boolean) {
    this.checked = checked;

    if (this.checked) {
      this.getMetricData();
    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override onDestroy() {
  }

  private getMetricData(): void {
    const years = this.appService.selectedYears();
    const states = this.appService.selectedStates();
    const metric = this.filterGroup.getRawValue().metric.value;
    const aggregate = this.filterGroup.getRawValue().aggregation;
    const includeNtlData = this.filterGroup.getRawValue().includeNtlData;


    this.restorationService.getMetricLayer(metric, aggregate, states, years, includeNtlData).pipe(
      map(res => {
        [this.legendMin, this.legendMax] = [res.scale.min, res.scale.max];
        const collection = {
          type: 'FeatureCollection',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          features: res.features.map((f: any) => {
            f.properties.popoverData = this.createPopover(f);
            return f;
          }),
        };
        return [collection, res.scale.toValue] satisfies [{ type: string, features: IceLayerFeature[] }, (value: number) => number];
      }),
      take(1),
    )
      .subscribe(([featureCollection, normalize]: [{ type: string, features: IceLayerFeature[] }, (value: number) => number]) => {
        const features = new GeoJSON().readFeatures(featureCollection);
        const domain = [];
        for (let i = 1; i < this.colors.length; i++) {
          domain.push(i / this.colors.length);
        }

        const colorScale = scaleThreshold<number, string>(domain, this.colors);
        const hatchPattern = (() => {
          const canvas = document.createElement('canvas');
          canvas.width = 12;
          canvas.height = 12;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return undefined;
          }

          // Use thicker, higher-contrast strokes and a larger tile for a clearer NTL hatch.
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, 12);
          ctx.lineTo(12, 0);
          ctx.stroke();
          return ctx.createPattern(canvas, 'repeat') ?? undefined;
        })();

        this.mapLayer().renderedLayer = new VectorLayer({
          source: new VectorSource({
            features: features as Feature<Geometry>[],
            attributions: this.mapLayer().buildAttribution(),
          }),
          properties: {
            name: this.mapLayer().displayName,
          },
          opacity: 0.8,
          style: (feature, resolution: number) => {
            const properties = feature.getProperties() as { tastiDetails?: MetricDetails, ntlDetails?: MetricDetails };
            const legendValue = properties.tastiDetails?.[aggregate];
            const hasNTL = properties.ntlDetails?.[aggregate] !== undefined;
            const color = (legendValue === undefined) ? '#444' : colorScale(normalize(legendValue));

            const baseStyle = new Style({
              stroke: new Stroke({
                color: 'black',
                width: 2,
              }),
              fill: new Fill({
                color: color,
              }),
              text: legendValue !== undefined ? new Text({
                text: (resolution > 3000) ? '' : Intl.NumberFormat('en', { notation: 'compact' }).format(legendValue ?? 0),
              }) : undefined,
            });

            if (hasNTL) {
              return [
                baseStyle,
                new Style({
                  fill: new Fill({ color: hatchPattern }),
                }),
              ];
            }

            return baseStyle;
          },
        });
        MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      });
  }


  override changeOpacity(opacity: number) {
    this.opacity = opacity;
    (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).setOpacity(this.opacity / 100);
  }

  public exportDataAsCsv(event: MouseEvent): void {
    event.stopPropagation();
    this.mapLayer().downloadFeaturesAsCsv();

  }

  public getLegendMeasurement() {
    const selectedMetric = this.filterGroup.get('metric')?.value;
    if (selectedMetric) {
      const metric = this.metrics.find(m => m.measurement === selectedMetric.measurement);
      return metric ? `${metric.measurement}` : 'No measurement';
    }

    return 'No measurement';
  }

  private createPopover(feature: MetricLayerFeature): PopoverData[] {
    const popoverData: PopoverData[] = [];
    const formatNumber = (x: number): string => {
      const formatted = this.numberPipe.transform(x, '1.2-2');
      return formatted ? '$' + formatted : 'Not Available';
    };
    popoverData.push(new PopoverData({ displayText: 'State', displayValue: feature.properties.stateAbbreviation }));
    popoverData.push(new PopoverData({ displayText: 'County', displayValue: feature.properties.county }));
    popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' })); // spacer
    const tasti = feature.properties.tastiDetails;
    const ntl = feature.properties.ntlDetails;
    if (tasti) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'TASTI-GRID' }));
      popoverData.push(new PopoverData({ displayText: 'Min', displayValue: formatNumber(tasti.min) }));
      popoverData.push(new PopoverData({ displayText: 'Max', displayValue: formatNumber(tasti.max) }));
      popoverData.push(new PopoverData({ displayText: 'Average', displayValue: formatNumber(tasti.avg) }));
      popoverData.push(new PopoverData({ displayText: 'Sum', displayValue: formatNumber(tasti.sum) }));
    }
    if (tasti && ntl) {
      popoverData.push(new PopoverData({ displayText: '\u00A0', displayValue: '\u00A0' })); // spacer
    }
    if (ntl) {
      popoverData.push(new PopoverData({ displayText: 'Source', displayValue: 'Night Time Lights' }));
      popoverData.push(new PopoverData({ displayText: 'Min', displayValue: formatNumber(ntl.min) }));
      popoverData.push(new PopoverData({ displayText: 'Max', displayValue: formatNumber(ntl.max) }));
      popoverData.push(new PopoverData({ displayText: 'Average', displayValue: formatNumber(ntl.avg) }));
      popoverData.push(new PopoverData({ displayText: 'Sum', displayValue: formatNumber(ntl.sum) }));
    }
    return popoverData;
  }


}
